import React, { useEffect, useState } from 'react';
import { RootState, AppDispatch } from '../store';
import { useSelector, useDispatch } from 'react-redux';

import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';
import { VpcService } from '@inter-ikea-kompis/services';
import OpenDesignStateEnum from '@inter-ikea-kompis/component-open-design-code/lib/enums/OpenDesignCodeStateEnum';

import {
	Settings,
	Platform,
	DeviceType,
	RangeSettings,
	SofaRangeName,
	WriteDirection,
	ShoppingItem,
} from 'sofa-reborn-react';
import OpenDesign from '../components/OpenDesign';
import {
	setGalleryPredefinedConfigurationId,
	setPersistedConfiguration,
	setGalleryVpcCode,
} from '../store/sofaConfiguration/actions';
import { setShowOpenDesign } from '../store/openDesign/actions';
import { hidePopOver } from '../store/popOver/actions';
import { clearAdditionalCoverItems, setAdditionalCoverItems, setCoverOnly } from '../store/cover';
import { setZipCode } from '../store/localization/actions';
import { UrlService } from '../services';

const OpenDesignContainer: React.FC = () => {
	const additionalCoverItems = useSelector<RootState, ShoppingItem[]>(
		(state) => state.cover.additionalCoverItems
	);
	const [openDesignState, setOpenDesignState] = useState(OpenDesignStateEnum.default);

	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const showOpenDesign = useSelector<RootState, boolean>(
		(state) => state.openDesign.showOpenDesign
	);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const [visible, setVisible] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	const onOpenDesignClick = async (): Promise<void> => {
		dispatch(setShowOpenDesign(!showOpenDesign));
		dispatch(clearAdditionalCoverItems());
		setOpenDesignState(OpenDesignStateEnum.default);
	};

	const onOpenVpcCode = async (vpcCode: string): Promise<void> => {
		setOpenDesignState(OpenDesignStateEnum.loading);

		let vpcConfiguration;
		let vpcError;

		try {
			vpcConfiguration = await new VpcService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
				applicationName: settings.applicationName,
				dexfApplicationId: settings.dexfApplicationId,
				dexfConfigurationVersion: settings.dexfConfigurationVersion,
			}).getConfiguration(vpcCode);
		} catch (error) {
			vpcError = error;
		}

		if (vpcConfiguration && vpcConfiguration.application.toLowerCase() === rangeSettings.rangeId) {
			dispatch(
				setZipCode(
					vpcConfiguration.configuration.zipCode ? vpcConfiguration.configuration.zipCode : null,
					false
				)
			);
			dispatch(setPersistedConfiguration(null));
			dispatch(setGalleryPredefinedConfigurationId(vpcCode));
			dispatch(
				hidePopOver({
					type: 'all',
				})
			);

			if (coverOnly) {
				dispatch(setCoverOnly(false));
			}

			/* Add # with vpc code to url */
			const newHash = UrlService.getReplacedIdHash(vpcCode.toUpperCase());
			UrlService.setBrowserWindowHash(newHash);
			dispatch(setGalleryVpcCode());

			onClose();
		} else {
			setOpenDesignState(OpenDesignStateEnum.failed);
		}

		if (vpcConfiguration.configuration.additionalCoverItems) {
			vpcConfiguration.configuration.additionalCoverItems.concat(additionalCoverItems);
			dispatch(setAdditionalCoverItems(vpcConfiguration.configuration.additionalCoverItems));
		}

		if (vpcError) {
			throw vpcError;
		}
	};

	const onClose = () => {
		setTimeout(() => {
			dispatch(setShowOpenDesign(false));
			setOpenDesignState(OpenDesignStateEnum.default);
			setVisible(false);
		}, 100);
	};

	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		if (showOpenDesign) {
			timeout = setTimeout(() => {
				setVisible(true);
			}, 100);
		}

		return (): void => {
			setVisible(false);
			clearTimeout(timeout);
		};
	}, [showOpenDesign]);

	return (
		<OpenDesign
			platform={platform}
			deviceType={deviceType}
			writeDirection={writeDirection}
			kompisSettings={kompisSettings}
			kompisTranslations={kompisTranslations}
			showOpenDesign={visible}
			sofaRangeName={rangeSettings.rangeName as SofaRangeName}
			openDesignState={openDesignState}
			onOpenDesignClick={onOpenDesignClick}
			onOpenVpcCode={onOpenVpcCode}
			onClose={onClose}
		/>
	);
};

export default OpenDesignContainer;
